import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { formatUsd } from '#/shared/lib/format'
import { cn } from '#/shared/lib/utils'
import { Badge } from '#/shared/ui/badge'
import { Button } from '#/shared/ui/button'
import { DatePicker } from '#/shared/ui/date-picker'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
} from '#/shared/ui/dialog'
import {
  CardIcon,
  CashAppIcon,
  ChevronDownIcon,
  CloseIcon,
  PayPalIcon,
  PlusIcon,
  VenmoIcon,
  ZelleIcon,
} from '#/shared/ui/icons'
import { Input } from '#/shared/ui/input'
import { Label } from '#/shared/ui/label'
import { Popover } from '#/shared/ui/popover'
import type { SelectOption } from '#/shared/ui/select'
import { Skeleton } from '#/shared/ui/skeleton'
import { Textarea } from '#/shared/ui/textarea'

import {
  PAYMENT_DIRECTION,
  PAYMENT_METHOD,
  PAYMENT_TYPE,
} from './payment-values'
import { usePaymentMethodOptions } from './use-method-options'
import type {
  PaymentDirection,
  PaymentMethodType,
  PaymentType,
} from './payment-values'

interface CreatePaymentFormValues {
  type: PaymentType
  direction: PaymentDirection
  amount: number
  surcharge: number | null
  discount: number | null
  methods: PaymentMethodType[]
  date: Date | null
  memo?: string
}

interface CreatePaymentModalProps {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  defaultValues?: Partial<CreatePaymentFormValues>
  methodOptions?: SelectOption[]
  onSubmit?: (values: CreatePaymentFormValues) => void | Promise<void>
  isSubmitting?: boolean
}

const TYPE_OPTIONS: ReadonlyArray<{ value: PaymentType; labelKey: string }> = [
  { value: PAYMENT_TYPE.income, labelKey: 'editor.payment.modal.type.income' },
  { value: PAYMENT_TYPE.refund, labelKey: 'editor.payment.modal.type.refund' },
  {
    value: PAYMENT_TYPE.carrier_pay,
    labelKey: 'editor.payment.modal.type.carrierPay',
  },
]

const DIRECTION_OPTIONS: ReadonlyArray<{
  value: PaymentDirection
  labelKey: string
}> = [
  {
    value: PAYMENT_DIRECTION.customer_to_broker,
    labelKey: 'editor.payment.modal.direction.customerToBroker',
  },
  {
    value: PAYMENT_DIRECTION.broker_to_customer,
    labelKey: 'editor.payment.modal.direction.brokerToCustomer',
  },
  {
    value: PAYMENT_DIRECTION.broker_to_carrier,
    labelKey: 'editor.payment.modal.direction.brokerToCarrier',
  },
]

type MethodIcon = typeof CardIcon

const METHOD_ICON: Record<string, MethodIcon | undefined> = {
  [PAYMENT_METHOD.card]: CardIcon,
  [PAYMENT_METHOD.zelle]: ZelleIcon,
  [PAYMENT_METHOD.cashapp]: CashAppIcon,
  [PAYMENT_METHOD.venmo]: VenmoIcon,
  [PAYMENT_METHOD.paypal]: PayPalIcon,
}

const METHOD_SKELETON_WIDTHS = ['w-16', 'w-20', 'w-18', 'w-14', 'w-22'] as const

function toAmountNumberOrNull(raw: unknown): number | null {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null
  if (typeof raw !== 'string' || raw === '') return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

function useControlledOpen(
  open?: boolean,
  onOpenChange?: (open: boolean) => void,
) {
  const [internal, setInternal] = React.useState(false)
  const isControlled = open !== undefined
  const value = isControlled ? open : internal
  const setValue = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setInternal(next)
      onOpenChange?.(next)
    },
    [isControlled, onOpenChange],
  )
  return [value, setValue] as const
}

const DEFAULT_VALUES: CreatePaymentFormValues = {
  type: PAYMENT_TYPE.income,
  direction: PAYMENT_DIRECTION.customer_to_broker,
  amount: 0,
  surcharge: null,
  discount: null,
  methods: [],
  date: null,
  memo: undefined,
}

const DEFAULT_DUE_DATE_OFFSET_DAYS = 5

function defaultDueDate(): Date {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  d.setDate(d.getDate() + DEFAULT_DUE_DATE_OFFSET_DAYS)
  return d
}

function buildInitialValues(
  defaults: Partial<CreatePaymentFormValues> | undefined,
): CreatePaymentFormValues {
  return { ...DEFAULT_VALUES, date: defaultDueDate(), ...(defaults ?? {}) }
}

function TypeSegment({
  active,
  label,
  onClick,
}: {
  active: boolean
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex h-10 flex-1 cursor-pointer items-center justify-center rounded-md border px-2.5 text-sm transition-colors',
        active
          ? 'border-primary bg-blue-100 font-medium text-primary'
          : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50',
      )}
    >
      {label}
    </button>
  )
}

function TypeField({
  value,
  onChange,
}: {
  value: PaymentType
  onChange: (value: PaymentType) => void
}) {
  const { t } = useTranslation()
  return (
    <FieldShell label={t('editor.payment.modal.fields.type')}>
      <div className="flex w-full gap-2">
        {TYPE_OPTIONS.map((option) => (
          <TypeSegment
            key={option.value}
            active={value === option.value}
            label={t(option.labelKey)}
            onClick={() => onChange(option.value)}
          />
        ))}
      </div>
    </FieldShell>
  )
}

function DirectionField({
  value,
  onChange,
}: {
  value: PaymentDirection
  onChange: (value: PaymentDirection) => void
}) {
  const { t } = useTranslation()
  const labelFor = (v: PaymentDirection) => {
    const found = DIRECTION_OPTIONS.find((o) => o.value === v)
    return found ? t(found.labelKey) : ''
  }
  return (
    <FieldShell label={t('editor.payment.modal.fields.direction')}>
      <Popover
        // Portal to <body> so the dropdown escapes the dialog's clipping
        // (overflow-hidden) without forcing the dialog to overflow-visible —
        // which would let the body push the footer outside on short viewports.
        portal
        wrapperClassName="w-full"
        contentClassName="min-w-full rounded-md bg-white shadow-popover"
        trigger={(open) => (
          <button
            type="button"
            aria-haspopup="listbox"
            aria-expanded={open}
            className={cn(
              'flex h-8 w-full cursor-pointer items-center justify-between gap-2 rounded-md border border-gray-200 bg-white px-1.75 transition-colors focus-visible:outline-none',
              open && 'focus-ring',
            )}
          >
            <Badge
              variant="success"
              className="h-auto rounded px-1.5 py-0.75 text-xs font-normal uppercase"
            >
              {labelFor(value)}
            </Badge>
            <ChevronDownIcon
              className={cn(
                'size-2.5 shrink-0 text-gray-500 transition-transform duration-200',
                open && 'rotate-180',
              )}
            />
          </button>
        )}
      >
        {(close) => (
          <div className="flex w-full flex-col gap-0.5 p-1" role="listbox">
            {DIRECTION_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={option.value === value}
                onClick={() => {
                  onChange(option.value)
                  close()
                }}
                className={cn(
                  'flex h-8 w-full cursor-pointer items-center rounded-sm px-2 text-left hover:bg-gray-50',
                  option.value === value && 'bg-gray-50',
                )}
              >
                <Badge
                  variant="success"
                  className="h-auto rounded px-1.5 py-0.75 text-xs font-normal uppercase"
                >
                  {t(option.labelKey)}
                </Badge>
              </button>
            ))}
          </div>
        )}
      </Popover>
    </FieldShell>
  )
}

function FieldShell({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="flex w-full flex-col gap-0.5">
      <Label size="sm">{label}</Label>
      {children}
    </div>
  )
}

const AMOUNT_INPUT_PROPS = {
  inputSize: 'sm' as const,
  numberFormat: true,
  placeholder: '0.00',
}

function MoneyCell({
  label,
  value,
  onChange,
  mask = '$',
  showZero = false,
}: {
  label: string
  value: number | null
  onChange: (next: number | null) => void
  mask?: string
  showZero?: boolean
}) {
  const display =
    value === null || (value === 0 && !showZero) ? '' : String(value)
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
      <span className="truncate text-xs uppercase text-gray-300">{label}</span>
      <Input
        {...AMOUNT_INPUT_PROPS}
        mask={mask}
        value={display}
        onChange={(e) => onChange(toAmountNumberOrNull(e.target.value))}
      />
    </div>
  )
}

function AmountCard({
  amount,
  surcharge,
  discount,
  onAmountChange,
  onSurchargeChange,
  onDiscountChange,
}: {
  amount: number
  surcharge: number | null
  discount: number | null
  onAmountChange: (n: number) => void
  onSurchargeChange: (n: number | null) => void
  onDiscountChange: (n: number | null) => void
}) {
  const { t } = useTranslation()
  const total = Math.max(
    0,
    amount + (amount * (surcharge ?? 0)) / 100 - (discount ?? 0),
  )
  return (
    <FieldShell label={t('editor.payment.modal.fields.amount')}>
      <div className="flex w-full flex-col gap-3 rounded-md bg-gray-50 p-3">
        <div className="flex items-start gap-2.5">
          <MoneyCell
            label={t('editor.payment.modal.amount.amount')}
            value={amount}
            onChange={(n) => onAmountChange(n ?? 0)}
          />
          <MoneyCell
            label={t('editor.payment.modal.amount.discount')}
            value={discount}
            onChange={onDiscountChange}
            showZero
          />
          <MoneyCell
            label={t('editor.payment.modal.amount.surcharge')}
            value={surcharge}
            onChange={onSurchargeChange}
            mask=""
            showZero
          />
        </div>
        <span aria-hidden className="h-px w-full bg-gray-200" />
        <div className="flex items-center justify-between">
          <span className="text-xs uppercase text-gray-300">
            {t('editor.payment.modal.amount.totalCharge')}
          </span>
          <span className="text-sm font-medium text-gray-900">
            {formatUsd(total, { showCents: true })}
          </span>
        </div>
      </div>
    </FieldShell>
  )
}

function MethodChip({
  active,
  label,
  value,
  onClick,
}: {
  active: boolean
  label: string
  value: string
  onClick: () => void
}) {
  const Icon = METHOD_ICON[value]
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex h-8 shrink-0 cursor-pointer items-center gap-1.25 rounded-md border px-3 text-sm transition-colors',
        active
          ? 'border-primary bg-blue-100 text-primary'
          : 'border-gray-200 bg-white text-gray-500 hover:bg-gray-50',
      )}
    >
      {Icon && <Icon className="size-4 shrink-0" aria-hidden />}
      {label}
    </button>
  )
}

function MethodChipSkeleton({ width }: { width: string }) {
  return (
    <Skeleton
      className={cn('h-8 shrink-0 rounded-md border border-gray-200', width)}
    />
  )
}

function MethodField({
  value,
  options,
  loading,
  onToggle,
}: {
  value: PaymentMethodType[]
  options: SelectOption[]
  loading: boolean
  onToggle: (method: PaymentMethodType) => void
}) {
  const { t } = useTranslation()
  const selected = new Set(value)
  return (
    <FieldShell label={t('editor.payment.modal.fields.method')}>
      <div className="flex w-full flex-wrap gap-2">
        {loading
          ? METHOD_SKELETON_WIDTHS.map((width, index) => (
              <MethodChipSkeleton key={index} width={width} />
            ))
          : options.map((option) => (
              <MethodChip
                key={option.value}
                active={selected.has(option.value as PaymentMethodType)}
                label={option.label}
                value={option.value}
                onClick={() => onToggle(option.value as PaymentMethodType)}
              />
            ))}
      </div>
    </FieldShell>
  )
}

function DateField({
  value,
  onChange,
}: {
  value: Date | null
  onChange: (date: Date | null) => void
}) {
  const { t } = useTranslation()
  return (
    <FieldShell label={t('editor.payment.modal.fields.date')}>
      <DatePicker
        value={value ?? undefined}
        onValueChange={(next) => onChange(next ?? null)}
        dateFormat="MMM D, YYYY"
        instantApply
      />
    </FieldShell>
  )
}

function MemoField({
  value,
  onChange,
}: {
  value: string
  onChange: (value: string) => void
}) {
  const { t } = useTranslation()
  return (
    <FieldShell label={t('editor.payment.modal.fields.memo')}>
      <Textarea
        textareaSize="sm"
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t('editor.payment.modal.memoPlaceholder')}
      />
    </FieldShell>
  )
}

function ModalHeader({
  title,
  closeLabel,
}: {
  title: string
  closeLabel: string
}) {
  return (
    <header className="flex h-13 shrink-0 items-center justify-between gap-2.5 border-b border-gray-200 px-5 py-2.5">
      <DialogTitle className="text-lg font-semibold text-gray-900">
        {title}
      </DialogTitle>
      <DialogClose
        aria-label={closeLabel}
        className="flex size-8 cursor-pointer items-center justify-center rounded-md text-gray-500 hover:bg-gray-50 focus-visible:outline-none focus-visible:focus-ring"
      >
        <CloseIcon className="size-3.5" />
      </DialogClose>
    </header>
  )
}

function ModalFooter({
  cancelLabel,
  submitLabel,
  onSubmit,
  submitDisabled,
}: {
  cancelLabel: string
  submitLabel: string
  onSubmit: () => void
  submitDisabled?: boolean
}) {
  return (
    <footer className="flex shrink-0 items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
      <DialogClose asChild>
        <Button
          type="button"
          variant="outline"
          size="xs"
          pressEffect
          className="px-3.75"
        >
          {cancelLabel}
        </Button>
      </DialogClose>
      <Button
        type="button"
        size="xs"
        pressEffect
        className="gap-2 px-3.75"
        onClick={onSubmit}
        disabled={submitDisabled}
      >
        <PlusIcon className="size-2.5" />
        {submitLabel}
      </Button>
    </footer>
  )
}

interface FormBodyProps {
  values: CreatePaymentFormValues
  setValues: React.Dispatch<React.SetStateAction<CreatePaymentFormValues>>
  methodOptions: SelectOption[]
  methodOptionsLoading: boolean
}

function ModalBody({
  values,
  setValues,
  methodOptions,
  methodOptionsLoading,
}: FormBodyProps) {
  const update = <TKey extends keyof CreatePaymentFormValues>(
    key: TKey,
    next: CreatePaymentFormValues[TKey],
  ) => setValues((prev) => ({ ...prev, [key]: next }))
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4.5 overflow-y-auto p-5">
      <TypeField value={values.type} onChange={(n) => update('type', n)} />
      <DirectionField
        value={values.direction}
        onChange={(n) => update('direction', n)}
      />
      <AmountCard
        amount={values.amount}
        surcharge={values.surcharge}
        discount={values.discount}
        onAmountChange={(n) => update('amount', n)}
        onSurchargeChange={(n) => update('surcharge', n)}
        onDiscountChange={(n) => update('discount', n)}
      />
      <MethodField
        value={values.methods}
        options={methodOptions}
        loading={methodOptionsLoading}
        onToggle={(m) =>
          setValues((prev) => ({
            ...prev,
            methods: prev.methods.includes(m)
              ? prev.methods.filter((x) => x !== m)
              : [...prev.methods, m],
          }))
        }
      />
      <DateField value={values.date} onChange={(n) => update('date', n)} />
      <MemoField
        value={values.memo ?? ''}
        onChange={(n) => update('memo', n)}
      />
    </div>
  )
}

function useCreatePaymentForm(
  isOpen: boolean,
  defaultValues: Partial<CreatePaymentFormValues> | undefined,
) {
  const [values, setValues] = React.useState<CreatePaymentFormValues>(() =>
    buildInitialValues(defaultValues),
  )
  React.useEffect(() => {
    if (!isOpen) return
    setValues(buildInitialValues(defaultValues))
  }, [isOpen, defaultValues])
  return { values, setValues }
}

function CreatePaymentModal({
  open,
  onOpenChange,
  defaultValues,
  methodOptions,
  onSubmit,
  isSubmitting,
}: CreatePaymentModalProps) {
  const { t } = useTranslation()
  const [isOpen, setOpen] = useControlledOpen(open, onOpenChange)
  const { values, setValues } = useCreatePaymentForm(isOpen, defaultValues)
  const { options: fetchedOptions, isLoading: methodsLoading } =
    usePaymentMethodOptions(isOpen && methodOptions === undefined)
  const resolvedOptions = methodOptions ?? fetchedOptions
  const handleSubmit = () => {
    void onSubmit?.(values)
  }
  const submitDisabled =
    Boolean(isSubmitting) || values.amount <= 0 || values.methods.length === 0
  return (
    <Dialog open={isOpen} onOpenChange={setOpen}>
      <DialogContent className="max-w-[520px]">
        <ModalHeader
          title={t('editor.payment.modal.title')}
          closeLabel={t('editor.payment.modal.close')}
        />
        <ModalBody
          values={values}
          setValues={setValues}
          methodOptions={resolvedOptions}
          methodOptionsLoading={methodsLoading}
        />
        <ModalFooter
          cancelLabel={t('editor.payment.modal.cancel')}
          submitLabel={t('editor.payment.modal.submit')}
          onSubmit={handleSubmit}
          submitDisabled={submitDisabled}
        />
      </DialogContent>
    </Dialog>
  )
}

export { CreatePaymentModal }
export type { CreatePaymentModalProps, CreatePaymentFormValues }
